// plugins/client-logger.client.js
import Vue from 'vue';

const ENDPOINT = '/api/logs/browser';

/* =========================
 * utils
 * =======================*/
function safeStringify(obj) {
    const seen = new WeakSet();
    return JSON.stringify(obj, function (key, value) {
        if (typeof value === 'function') return undefined;
        if (typeof value === 'object' && value !== null) {
            if (seen.has(value)) return '[Circular]';
            seen.add(value);
        }
        return value;
    });
}

function snapshot(obj, keys = [], limit = 5) {
    if (!obj || typeof obj !== 'object') return obj;
    const out = {};
    for (const k of keys.slice(0, limit)) {
        const v = obj[k];
        if (v == null) continue;
        if (['string', 'number', 'boolean'].includes(typeof v)) out[k] = v;
        else if (Array.isArray(v)) out[k] = v.slice(0, 5);
        else if (typeof v === 'object') out[k] = '[Object]';
    }
    return out;
}

function clampStr(s, n = 1000) {
    if (typeof s !== 'string') return s;
    return s.length > n ? s.slice(0, n) + '…[truncated]' : s;
}

function baseMeta() {
    try {
        const url = new URL(window.location.href);
        const query = {};
        url.searchParams.forEach((v, k) => { query[k] = v; });
        return {
            path: window.location.pathname + window.location.search,
            referrer: document.referrer || undefined,
            userAgent: navigator.userAgent,
            viewport: { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio },
            query
        };
    } catch {
        return { userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined };
    }
}

function post(payload) {
    try {
        fetch(ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: safeStringify(payload),
            keepalive: true,
        }).catch(() => { });
    } catch { }
}

function markLogged(err) {
    try { Object.defineProperty(err, '__logged', { value: true, enumerable: false }); } catch { }
}

/* =========================
 * plugin
 * =======================*/
export default (ctx, inject) => {
    // regular function (not arrow) so `this` can be a Vue component instance
    function logError(err, message = 'Browser error', extra = {}) {
        const meta = { ...baseMeta() };

        // route + component context
        try {
            if (this && this._isVue) {
                const name =
                    this.$options?.name ||
                    this.$options?._componentTag ||
                    (this.$vnode && this.$vnode.tag) ||
                    'anonymous';
                meta.component = name;
                meta.componentData = snapshot(this.$data, Object.keys(this.$data || {}).slice(0, 10));
                meta.componentProps = snapshot(this.$props, Object.keys(this.$props || {}).slice(0, 10));
            }
            if (ctx?.route?.fullPath) meta.route = ctx.route.fullPath;
        } catch { }

        // caller-provided context (e.g., axios meta from your other component)
        meta.context = { ...extra };

        // core error info
        if (err) {
            meta.errorMessage = err.message || String(err);
            meta.errorStack = err.stack || undefined; // Send browser stack trace
            meta.errorName = err.name || 'Error';
        }

        post({ level: 'error', message, meta });
        if (err) markLogged(err);
    }

    inject('logError', logError);
    if (typeof window !== 'undefined') window.$logError = logError;

    // ===== Vue render/lifecycle errors =====
    Vue.config.errorHandler = (err, vm, info) => {
        const name =
            vm?.$options?.name ||
            vm?.$options?._componentTag ||
            (vm?.$vnode && vm.$vnode.tag) ||
            'anonymous';
        logError(err, 'vue.errorHandler', { info, component: name });
        console.log(err.message);
    };

    // ===== Global runtime errors =====
    if (process.client) {
        window.addEventListener('error', (e) => {
            // element resource errors in capture phase handled below
            const isElementTarget = e.target && (e.target !== window);
            if (isElementTarget) return;
            const err = e.error || new Error(e.message || 'window.onerror');
            if (err.__logged) return;
            logError(err, 'window.onerror', { colno: e.colno, lineno: e.lineno, filename: e.filename });
        });

        // Resource load errors are filtered out (too noisy)

        // unhandled promise rejections
        window.addEventListener('unhandledrejection', (e) => {
            const reason = e.reason instanceof Error ? e.reason : new Error(String(e.reason));
            if (reason.__logged) return;
            logError(reason, 'unhandledrejection');
        });
    }
};
